import { useState, useMemo, useEffect } from "react"

import { Radio } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { styled } from "styled-components"
import { BaseModal } from "shared/ui/BaseModal"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { selectIsPlansModalOpen } from "features/common/model/selectors/appSelectors"
import { setIsPlansModalOpen } from "features/common/model/slice/appSlice"
import { packagesSelector } from "features/team/model/selectors/teamSelectors"
import { checkoutSessionThunk, sendFeedbackThunk } from "features/team/model/slice/teamSlice"
import { useTranslation } from "react-i18next"

import { PlanCard } from "./PlanCard"
import { EnterpriseContactModal } from "./EnterpriseContactModal"

const formatStorageSize = (bytes: number): string => {
  const gb = bytes / (1024 * 1024 * 1024)
  const tb = bytes / (1024 * 1024 * 1024 * 1000)
  
  if (tb >= 1) {
    return `${(Math.round(tb * 10) / 10)} TB`
  }
  return `${Math.round(gb)} GB`
}

export const PlansModal = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const featuresListForFirstPlan = [
    t('l_plans_basicInfoThree'),
    t('l_plans_basicInfoFour'),
    t('l_plans_basicInfoFive')
  ]
  
  const featuresListForSecondPlan = [
    t('l_plans_proInfoThree'),
    t('l_plans_proInfoFour'),
    t('l_plans_proInfoFive')
  ]
  
  const featuresListForThirdPlan = [
    t('l_plans_enterpriseInfoThree'),
    t('l_plans_enterpriseInfoFour'),
    t('l_plans_enterpriseInfoFive')
  ]

  const plansDescriptions = [
    t('l_plans_basicDescription'),
    t('l_plans_proDescription'),
    t('l_plans_enterpriseDescription')
  ]
  
  const featuresLists = [
    featuresListForFirstPlan,
    featuresListForSecondPlan,
    featuresListForThirdPlan
  ]
  
  const isPlansModalOpen = useAppSelector(selectIsPlansModalOpen)
  const packages = useAppSelector(packagesSelector)

  const [planPeriod, setPlanPeriod] = useState<'month' | 'year'>('year')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isEnterpriseContactOpen, setIsEnterpriseContactOpen] = useState(false)

  const submitEnterpriseContact = (values: { name: string; email: string; message?: string }) => {
    dispatch(sendFeedbackThunk({ name: values.name, email: values.email, message: `${values.name}. ${values.message || 'Empty'}`, 
      successCallback: () => {
        setIsEnterpriseContactOpen(false)
      },
      errorCallback: () => {
        setIsEnterpriseContactOpen(false)
      } }))
  }

  const filteredPackages = useMemo(() => {
    return packages.filter((pkg) => pkg.period_type === planPeriod)
  }, [packages, planPeriod])

  const plans = useMemo(() => {
    return filteredPackages.map((pkg, index) => ({
      id: pkg.id,
      title: pkg.name,
      price: (
        <>
          {pkg.currency === 'USD' ? '$' : ''}

          {planPeriod === 'month' ? Number(pkg.cost) : `${(Number(pkg.cost) / 12)}`}

          <span>
            {t('l_plans_priceMonth')}
          </span>
        </>
      ),
      description: plansDescriptions[index],
      storage: formatStorageSize(pkg.size),
      membersCount: String(pkg.team_members_count),
    }))
  }, [filteredPackages, planPeriod])

  useEffect(() => {
    if (!isPlansModalOpen) {
      setPlanPeriod('year')
    }
  }, [isPlansModalOpen])

  const handleActivate = (planId: string) => {
    const targetPackage = filteredPackages.find((pkg) => pkg.id === planId)

    if (targetPackage?.name?.toLowerCase() === 'enterprise') {
      setIsEnterpriseContactOpen(true)
      return
    }

    setIsProcessing(true)
    dispatch(checkoutSessionThunk({ 
      packageID: planId, 
      callback: (url) => {
        window.location.href = url
      }, errorCallback: () => {
        setIsProcessing(false)
      } }))
  }

  const handleClose = () => {
    dispatch(setIsPlansModalOpen(false))
  }

  return (
    <SModal
      isOpen={isPlansModalOpen}
      onClose={handleClose}
      style={{ top: '0' }}
      width={window.innerWidth}
      closeIcon={false}
    >
      <SModalContent>
        <SCloseBtn onClick={handleClose}>
          <SCloseIcon />
        </SCloseBtn>

        <STitle>
          {t('l_plans_choosePlanTitle')}
        </STitle>

        <SPeriodRow>
          <SRadioGroup value={planPeriod}
            onChange={(e) => setPlanPeriod(e.target.value)}
          >
            <SRadio 
              value={'month'} 
            >
              {t('l_plans_forMonth')}
            </SRadio>

            <SRadio 
              value={'year'}
              className="ml-40"
            >
              {t('l_plans_forYear')}
            </SRadio>

            <SSaving>
              {t('l_plans_saveBadge')}
            </SSaving>
          </SRadioGroup>
        </SPeriodRow>

        <SPlansRow>
          {plans.map((plan, index) => (
            <PlanCard 
              key={plan.id}
              {...plan}
              featuresList={featuresLists[index]}
              onActivate={handleActivate}
              isProcessing={isProcessing}
              buttonText={t('a_plans_startPlan')}
            />
          ))}

          <PlanCard 
            id="enterprise"
            title={t('l_plans_nameEnterprise')}
            price={t('l_plans_enterpriseTitle')}
            description={plansDescriptions[2]}
            storage="4 TB"
            storageText={t('l_plans_planEnterpriseOne')}
            membersCount="Custom"
            featuresList={featuresLists[2]}
            onActivate={() => setIsEnterpriseContactOpen(true)}
            isProcessing={isProcessing}
            buttonText={t('a_plans_contactSales')}
            membersText={t('l_plans_enterpriseInfoTwo')}
          />
        </SPlansRow>
      </SModalContent>

      <EnterpriseContactModal
        open={isEnterpriseContactOpen}
        onCancel={() => setIsEnterpriseContactOpen(false)}
        onSubmit={submitEnterpriseContact}
      />
    </SModal>
  )
}

const SModal = styled(BaseModal)`
  max-width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;

  & > div {
    height: 100%;
  }

  .ant-modal-body {
    padding: 0;
    height: 100%;
  }

  .ant-modal-content {
    padding: 0;
    height: 100%;
    border-radius: 0;
  }
`

const SModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const STitle = styled.h1`
  font-size: 32px;
  line-height: 32px;
  font-weight: 600;
  color: #000000E0;
`

const SPeriodRow = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SRadioGroup = styled(Radio.Group)`
  display: flex;
  align-items: center;
  justify-content: center;
`

const SRadio = styled(Radio)`
  margin-inline-end: 0;

  &.ml-40 {
    margin-left: 40px!important;
  }
`

const SSaving = styled.div`
  background-color: #D9D9D9;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  background-color: #D9D9D9;
  padding: 0 8px;
  color: #000000E0;
  font-size: 14px;
  font-weight: 400;
  border-radius: 4px;
`

const SPlansRow = styled.div`
  margin-top: 40px;
  display: flex;
  gap: 20px;
`

const SCloseBtn = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0000000F;
  border-radius: 50%;
`

const SCloseIcon = styled(CloseOutlined)`
  color: #000000E0;
`